import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import type { Client, Project } from '~/types/models';

export function useSignedContractProjectLink(options: {
    client: MaybeRefOrGetter<Client | null | undefined>;
    projects: MaybeRefOrGetter<Project[]>;
    canMutate: MaybeRefOrGetter<boolean>;
    tenantId: MaybeRefOrGetter<string | null | undefined>;
    refresh: () => Promise<unknown>;
}) {
    const open = ref(false);
    const projectId = ref('');
    const loading = ref(false);
    const error = ref('');
    const projectOptions = computed(() => toValue(options.projects)
        .filter(project => {
            const client = toValue(options.client);
            return project.clientId === client?.id && (!project.contract || (
                project.contract.status === 'signed'
                && Boolean(client?.contract?.signedDocumentHash)
                && project.contract.signedDocumentHash === client?.contract?.signedDocumentHash
                && project.contract.version === client?.contract?.version
            ));
        })
        .map(project => ({ value: project.id, label: project.name || 'Untitled project' })));

    const selectedProject = computed(() => projectOptions.value.find(project => project.value === projectId.value));

    function show() {
        if (!toValue(options.canMutate)) return;
        projectId.value = projectOptions.value.length === 1 ? projectOptions.value[0]!.value : '';
        error.value = '';
        open.value = true;
    }

    async function link() {
        const client = toValue(options.client);
        if (loading.value || !toValue(options.canMutate) || !client?.contract || !projectId.value) return;
        loading.value = true;
        error.value = '';
        try {
            await $fetch('/api/contracts/link-project', {
                method: 'POST', headers: await getRequiredAuthHeaders(),
                body: { tenantId: toValue(options.tenantId), clientId: client.id, projectId: projectId.value, version: client.contract.version },
            });
            await options.refresh();
            open.value = false;
        } catch (failure: any) {
            error.value = failure?.data?.statusMessage || failure?.message || 'Unable to link contract. Retry to recheck onboarding if the agreement is already linked.';
        } finally {
            loading.value = false;
        }
    }
    return { open, projectId, selectedProject, loading, error, projectOptions, show, link };
}
